import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Divider,
  Link
} from '@mui/material';
import { styled } from '@mui/material/styles';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(4),
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
}));

const StyledSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

const StyledSubsection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  paddingLeft: theme.spacing(2),
}));

const Terms = () => {
  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="md">
        <StyledPaper>
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700, color: 'primary.main' }}>
              Asia Economic Times
            </Typography>
            <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
              Legal Disclaimer
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Last updated: {new Date().toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </Typography>
          </Box>

          <Divider sx={{ mb: 4 }} />

          {/* Section 1 */}
          <StyledSection>
            <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
              1. General Information
            </Typography>
            <Typography variant="body1" paragraph>
              The information, data, opinions, and materials provided on AsiaEconomicTimes.com (the "Website") are for general informational purposes only. The Website and its content are published in good faith for public awareness and educational use. Nothing contained on this Website shall be construed as financial, investment, legal, or professional advice of any kind. Visitors, readers, and users ("you") are advised to exercise independent judgment and, where appropriate, seek advice from licensed professionals before acting on any information presented.
            </Typography>
          </StyledSection>

          {/* Section 2 */}
          <StyledSection>
            <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
              2. Accuracy, Timeliness, and Completeness of Data
            </Typography>
            <Typography variant="body1" paragraph>
              While Asia Economic Times ("AET") endeavors to ensure that the information provided is obtained from reliable sources, data and market information may be delayed, incomplete, or subject to revision without notice. This includes — but is not limited to — market prices, exchange rates, stock indices, commodities data, central bank statistics, and reports derived from third-party sources such as international organizations (e.g., World Bank, IMF, United Nations), multilateral and bilateral development agencies, government departments and central banks, publicly available statistical bureaus, and financial market data vendors, media partners, and research firms.
            </Typography>
            <Typography variant="body1" paragraph>
              AET does not guarantee that any such data is accurate, current, verified, or suitable for any specific purpose. Market data may be subject to time delays or technical errors beyond the control of AET.
            </Typography>
          </StyledSection>

          {/* Section 3 */}
          <StyledSection>
            <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
              3. No Warranties
            </Typography>
            <Typography variant="body1" paragraph>
              All content on the Website is provided on an "as is" and "as available" basis without any warranties, express or implied, including but not limited to warranties of accuracy, completeness, or timeliness; warranties of merchantability, fitness for a particular purpose, or non-infringement; or warranties against errors, omissions, interruptions, or delays in service. AET does not warrant that the Website, its servers, or linked content are free of viruses or other harmful components.
            </Typography>
          </StyledSection>

          {/* Section 4 */}
          <StyledSection>
            <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
              4. Limitation of Liability
            </Typography>
            <Typography variant="body1" paragraph>
              To the fullest extent permitted by applicable law, Asia Economic Times, its owners, affiliates, editors, contributors, and data providers shall not be liable for any direct, indirect, incidental, consequential, special, punitive, or exemplary damages, loss of profits, data, or goodwill, business interruption, or any other loss arising from reliance upon or use of the Website or its contents. Use of the Website and reliance on any information is at your sole risk. AET expressly disclaims any responsibility or liability for actions taken or not taken based on any content or material available on the Website.
            </Typography>
          </StyledSection>

          {/* Section 5 */}
          <StyledSection>
            <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
              5. Third-Party Links and Data Providers
            </Typography>
            <Typography variant="body1" paragraph>
              The Website may include links to third-party websites, data feeds, or content providers. Such links are provided solely for reference and convenience. AET does not endorse or assume any responsibility for the accuracy, legality, or reliability of external sites or data feeds. Users who access such third-party content do so entirely at their own discretion and risk.
            </Typography>
          </StyledSection>

          {/* Section 6 */}
          <StyledSection>
            <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
              6. No Offer, Solicitation, or Endorsement
            </Typography>
            <Typography variant="body1" paragraph>
              No material on the Website constitutes or should be construed as an offer to buy or sell securities, commodities, or financial instruments; a solicitation to invest or engage in any transaction; or an endorsement, recommendation, or opinion regarding any financial product, institution, or policy. All commentary and analysis represent editorial perspectives and do not reflect investment or trading advice.
            </Typography>
          </StyledSection>

          {/* Section 7 */}
          <StyledSection>
            <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
              7. Jurisdiction and Legal Compliance
            </Typography>
            <Typography variant="body1" paragraph>
              This Disclaimer shall be governed by and construed in accordance with internationally recognized principles of digital publishing and media law, and where applicable, the laws of the jurisdiction in which Asia Economic Times operates. Users are responsible for compliance with local laws and regulations in their respective countries when accessing or using the Website.
            </Typography>
          </StyledSection>

          {/* Section 8 */}
          <StyledSection>
            <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
              8. Updates to this Disclaimer
            </Typography>
            <Typography variant="body1" paragraph>
              AET reserves the right to update, revise, or modify this Legal Disclaimer at any time without prior notice. The revised version will be effective immediately upon posting. Continued use of the Website constitutes acceptance of such changes.
            </Typography>
          </StyledSection>

          {/* Section 9 */}
          <StyledSection>
            <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
              9. Contact Information
            </Typography>
            <Typography variant="body1" paragraph>
              For questions or concerns regarding this Disclaimer, please contact:
            </Typography>
            <StyledSubsection>
              <Typography variant="body1" paragraph>
                <strong>Asia Economic Times Legal & Compliance Office</strong><br />
                Email: <Link href="mailto:legal@asiaeconomictimes.com" color="primary">legal@asiaeconomictimes.com</Link><br />
                Website: <Link href="https://www.asiaeconomictimes.com" color="primary" target="_blank" rel="noopener noreferrer">https://www.asiaeconomictimes.com</Link>
              </Typography>
            </StyledSubsection>
          </StyledSection>

          {/* Section 10 */}
          <StyledSection>
            <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
              10. Acknowledgment
            </Typography>
            <Typography variant="body1" paragraph>
              By accessing, browsing, or using AsiaEconomicTimes.com, you acknowledge that you have read, understood, and agreed to this Legal Disclaimer in its entirety.
            </Typography>
          </StyledSection>

          <Divider sx={{ mt: 4, mb: 2 }} />

          {/* Footer */}
          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Typography variant="body2" color="text.secondary">
              © {new Date().getFullYear()} Asia Economic Times. All rights reserved.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              This Legal Disclaimer is effective as of the last updated date shown above.
            </Typography>
          </Box>
        </StyledPaper>
      </Container>
    </Box>
  );
};

export default Terms;
